"use client";

import { cn } from "@newxsystems/shared";
import { Icon } from "@templates/design-system";
import { useId, useState } from "react";
import { VelvetRichText } from "../VelvetRichText/VelvetRichText";
import type { IAccordionListProps } from "./AccordionList.props";

export const AccordionList = ({ items, openBehavior }: IAccordionListProps) => {
  const firstOpen = items.findIndex((item) => item.initiallyOpen);
  const [openItems, setOpenItems] = useState<number[]>(
    openBehavior === "multiple-open"
      ? items.flatMap((item, index) => (item.initiallyOpen ? [index] : []))
      : firstOpen >= 0
        ? [firstOpen]
        : [],
  );
  const baseId = useId();
  const toggle = (index: number) =>
    setOpenItems((current) =>
      current.includes(index)
        ? current.filter((item) => item !== index)
        : openBehavior === "multiple-open"
          ? [...current, index]
          : [index],
    );

  return (
    <div className="border-t border-parchment">
      {items.map((item, index) => {
        const open = openItems.includes(index);
        const triggerId = `${baseId}-trigger-${index}`;
        const panelId = `${baseId}-panel-${index}`;
        return (
          <div className="border-b border-parchment/25" key={index}>
            <h3>
              <button
                aria-controls={panelId}
                aria-expanded={open}
                className="group grid min-h-24 w-full grid-cols-[2.5rem_1fr_auto] items-center gap-4 py-6 text-left font-velvet-display text-2xl font-black uppercase leading-[0.95] tracking-[-0.025em] transition-colors hover:text-thread focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring md:grid-cols-[4rem_1fr_auto] md:text-4xl"
                id={triggerId}
                onClick={() => toggle(index)}
                type="button"
              >
                <span className="font-velvet-body text-sm italic text-smoke">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <span>{item.title}</span>
                <Icon
                  className={cn(
                    "size-10 shrink-0 border border-parchment/30 p-2 text-thread transition-[transform,border-color] duration-200 group-hover:border-thread",
                    open && "rotate-45",
                  )}
                  name="plus"
                />
              </button>
            </h3>
            <div
              aria-hidden={!open}
              aria-labelledby={triggerId}
              className={cn(
                "grid transition-[grid-template-rows] duration-300 ease-out",
                open ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
              )}
              id={panelId}
              inert={!open}
              role="region"
            >
              <div className="overflow-hidden">
                <VelvetRichText
                  className="max-w-[65ch] pb-8 pl-[4.1rem] text-base text-smoke md:pl-20 md:text-lg"
                  content={item.content}
                />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

AccordionList.displayName = "Content::AccordionList";
